import { useCallback, useEffect, useRef } from 'react';

import { LoadingScreen } from '#modules/ui/LoadingScreen';

import { useDynamicAssessmentAnalytics } from '../../hooks/useDynamicAssessmentAnalytics';
import { useDynamicAssessmentSession } from '../../hooks/useDynamicAssessmentSession';
import type { Answers } from '../../types/assessmentQuestion';
import type { DynamicNodeProgress } from '../../types/dynamicAssessment';
import { toDynamicAssessmentQuestions } from '../../utils/toDynamicAssessmentQuestions';
import { AssessmentContent } from '../AssessmentContent/AssessmentContent';
import { InfoGateScreen } from '../InfoGateScreen/InfoGateScreen';

/** Why the dynamic flow handed control back to the parent. */
export type DynamicAssessmentDoneReason = 'exhausted' | 'error';

/** Terminal outcomes the dynamic pass surfaced, handed to the parent on done. */
export type DynamicAssessmentResult = {
  /**
   * Set when phq_gad_v1's terminal carried a crisis-line result (Sparta's
   * requiresCrisisLine + qualifiedProgramUrns). The parent defers it into the
   * rules engine so assessment completion routes to the crisis program screen.
   */
  crisisProgramUrns?: string[];
  /**
   * The dynamic flow keys that completed in this pass — the backend's `/flows/`
   * statuses unioned with the flows the pass itself drove to a terminal (see
   * `useDynamicAssessmentSession`). The parent latches it so forward nav skips a
   * standalone screener (pre-diabetes / phq-gad) only when its flow actually
   * completed — guarding against silently dropping a screener whose flow was
   * unpublished/abandoned.
   */
  completedFlowKeys?: readonly string[];
};

type DynamicAssessmentFlowProps = {
  /**
   * Called once the dynamic flows hand back control. The parent decides what's
   * next — in the test arm it runs the legacy screener flow. The `reason` lets
   * it tell a clean finish ('exhausted') from a failure ('error'): on 'error'
   * the answers may not have been written to the legacy tables (no terminal
   * effect fired), so the parent falls back to the full legacy survey walk
   * instead of excluding the dynamic assessments.
   */
  onDone: (reason: DynamicAssessmentDoneReason, result?: DynamicAssessmentResult) => void;
  /**
   * Current member UUID, for analytics attribution only. A plain prop rather than
   * a context read, so a host doesn't have to model a user just to serve the flows.
   *
   * Two behaviours, split by what the event is for:
   *   - **Funnel events wait for it** — `dynamic-assessment-started`,
   *     `-completed`, `dynamic-assessment-screener-started-*` and
   *     `dynamic-assessment-screener-completed-*` don't fire at all until it's
   *     known, so a rate never mixes attributed and anonymous sessions.
   *   - **Everything else fires without it**, omitting the `uuid` property
   *     entirely (not sending it as `undefined`).
   */
  userUuid?: string;
  /**
   * Which dynamic flows to serve, in order. Defaults to the full onboarding assessment
   * region (`DYNAMIC_ONBOARDING_FLOW_KEYS`). Pass a subset to serve fewer — a host
   * app that only wants one screener passes `['aom_screener_v1']`, and `onDone`
   * fires with 'exhausted' as soon as that flow completes or skips.
   *
   * Safe to pass inline: the session hook keys on the array's CONTENTS, not its
   * identity, so a literal can't retrigger the request pass.
   */
  flowKeys?: readonly string[];
  /**
   * Reports this node's progress along its flow, verbatim from the wire — the
   * host renders the bar, this module never draws one. `null` means the flow
   * sent no `progress` (i.e. its author has not set `vidaProgressBar`), so a
   * host can hide its bar rather than freeze it at a stale value.
   *
   * Contract — all three matter, and a host bar misbehaves if any is missed:
   *   1. Fires for every node the member is actually shown, a `vidaInfoScreen`
   *      gate included. A gate the session hook auto-acks silently never
   *      surfaces here, and doesn't need to: it carries zero weight
   *      server-side, so the next node this fires for already accounts for it.
   *   2. Idempotent per node: an incidental re-render (a keystroke, a parent
   *      context update) must not re-notify.
   *   3. Emits `null` when a resolved node has no `progress`, so a host can
   *      tell "no bar for this flow" from "not loaded yet".
   */
  onProgress?: (progress: DynamicNodeProgress | null) => void;
};

/**
 * Drives the onboarding dynamic-assessment flows sequentially (health conditions
 * → goals → AOM screeners → diabetes decision → pre-diabetes → phq/gad) — the
 * test-arm `/step/dynamic-assessment` step. Mounted by
 * DynamicAssessmentScreen only when `onb_use_dynamic_assessment` is on, so it never
 * needs to read the flag itself.
 *
 * The hook handles gate auto-transition and instant-skip terminals transparently;
 * the screen only renders when there is a question node to show. On exhaustion
 * (all flows done or skipped) or on error, calls `onDone` with the reason — the
 * parent then runs the legacy flow for the screeners dynamic assessment doesn't
 * support yet (and, on error, latches the fallback so the whole assessment region
 * re-runs legacy). A phq_gad_v1 crisis terminal rides along in the result.
 */
export function DynamicAssessmentFlow({
  onDone,
  userUuid,
  flowKeys,
  onProgress,
}: DynamicAssessmentFlowProps) {
  const { trackEvent } = useDynamicAssessmentAnalytics();
  const {
    content,
    isLoading,
    isSubmitting,
    isExhausted,
    completedFlowKeys,
    answeredFlows,
    isResumed,
    crisisProgramUrns,
    error,
    submit,
    continueGate,
  } = useDynamicAssessmentSession(flowKeys);

  const startedSessionRef = useRef<string | null>(null);
  // The last node identity `onProgress` was fired for, so the effect below
  // re-running doesn't re-notify a host for the SAME node. `content` itself is
  // `useState`-held in `useDynamicAssessmentSession` and referentially stable
  // between renders — the thing actually likely to change on an otherwise
  // unrelated render is `onProgress`, since a host passing an inline callback
  // (as VRN's own screen does: `onProgress={(p) => setProgress(...)}`) hands
  // in a new function identity every render, and it's in the effect's own
  // dependency array. Keyed on node identity (`flowKey` +
  // `currentNodeExternalId`) rather than on `content`, since a resumed
  // session's `content` object for the SAME node is also a distinct
  // reference from whatever produced the last one.
  const lastReportedNodeRef = useRef<string | null>(null);
  // Flows whose `dynamic-assessment-screener-completed-*` has already been
  // emitted this mount, so a re-render (or a later append to `answeredFlows`)
  // re-emits nothing.
  const completedReportedRef = useRef<Set<string>>(new Set());
  // Guards the once-per-mount funnel events (arm entry + terminal handoff) AND the
  // onDone handoff against a double-fire — StrictMode double-invoke, or
  // `shouldAdvance` re-flipping when `crisisProgramUrns` resolves in the same
  // render as a terminal. Explicit so the once-per-mount contract holds.
  const armEnteredRef = useRef(false);
  const doneFiredRef = useRef(false);

  const onContinue = useCallback(
    async (answers: Answers) => {
      // `submit` swallows its own errors and surfaces them via `error`, which the
      // shouldAdvance effect below turns into onDone('error') — so there's no throw
      // to catch here (a submit failure hands off to the parent, like the legacy flow).
      await submit(answers);
    },
    [submit]
  );

  // All flows done ('exhausted') or a load/submit error ('error') → hand control
  // back to the parent, telling it which so it can decide whether to fall back to
  // legacy. A captured crisis outcome rides along on the clean finish.
  const shouldAdvance = !isLoading && (isExhausted || error != null);

  // Arm-entry event: fires once when the test arm mounts this flow, BEFORE any
  // load/submit-failed event can fire — so it's the clean denominator for the
  // completion and fallback rates (unlike dynamic-assessment-screener-started,
  // which a first-flow load failure would skip).
  useEffect(() => {
    if (armEnteredRef.current || !userUuid) return;
    armEnteredRef.current = true;
    trackEvent('dynamic-assessment-started', { uuid: userUuid });
  }, [userUuid, trackEvent]);

  // Terminal handoff. On a clean finish emit `dynamic-assessment-completed` (the
  // numerator for the WHOLE-assessment completion rate — the per-screener one is
  // `dynamic-assessment-screener-started-*` / `dynamic-assessment-screener-completed-*`);
  // the error path's counters live in the session hook, and the legacy re-serve
  // is tracked by LegacyAssignedScreenerFlow's own `legacy-screener-*` events.
  // ref-guarded so the funnel events + onDone fire exactly once. A captured crisis
  // outcome rides along in the result.
  useEffect(() => {
    if (!shouldAdvance || doneFiredRef.current) return;
    doneFiredRef.current = true;
    const reason = error != null ? 'error' : 'exhausted';
    // Gated on `userUuid` for the same reason `dynamic-assessment-started` is:
    // these two are the denominator and numerator of the completion rate, so
    // they have to agree on when they fire. Ungated, a host that never supplies
    // a UUID (or supplies it late) emits a `-completed` with no matching
    // `-started` and the rate reads above 100%.
    //
    // `onDone` deliberately stays outside the gate — analytics attribution must
    // never decide whether control returns to the parent.
    if (reason === 'exhausted' && userUuid) {
      trackEvent('dynamic-assessment-completed', { uuid: userUuid });
    }
    onDone(reason, { crisisProgramUrns, completedFlowKeys });
  }, [shouldAdvance, error, crisisProgramUrns, completedFlowKeys, onDone, userUuid, trackEvent]);

  // Fire "dynamic-assessment-screener-started" once per flow as its question
  // loads (deduped on the session UUID), mirroring the legacy flow's
  // `legacy-screener-started-*`. Keyed by the dynamic flowKey — intentionally
  // NOT remapped to the legacy screener_name: the two arms emit distinct
  // identifiers by design (which also keeps them distinguishable in analysis);
  // reconcile them at the dashboard layer. Prefixed `dynamic-assessment-` (as is
  // every event this flow emits) so it can never collide with the legacy arm's
  // own event — the two used to share the unprefixed `screener-started-*` name,
  // which aliased for `aom_screener_v1` (present verbatim in both arms' key sets).
  useEffect(() => {
    if (content && userUuid && startedSessionRef.current !== content.sessionUuid) {
      startedSessionRef.current = content.sessionUuid;
      trackEvent(`dynamic-assessment-screener-started-${content.flowKey}`, {
        uuid: userUuid,
        screenerName: content.flowKey,
        sessionUuid: content.sessionUuid,
        resumed: isResumed,
      });
    }
  }, [content, userUuid, trackEvent, isResumed]);

  // Per-screener completion: one event per flow the member actually answered,
  // emitted as the flow terminates rather than batched at exhaustion, so a member
  // who abandons midway still reports the screeners they did finish. Paired with
  // `dynamic-assessment-screener-started-*`, this is the per-screener funnel;
  // `dynamic-assessment-*` remains the whole-assessment one.
  //
  // The hook decides what qualifies (answered, this mount only — see
  // `answeredFlows`); this effect only turns new entries into events. Gated on
  // `userUuid` to match `dynamic-assessment-screener-started-*`, since an
  // ungated completion against a gated start reads as a rate above 100%. Carries
  // the same `sessionUuid` as that start event — the hook remembers it per flow,
  // because `content` has moved on to the next screener by the time this fires.
  useEffect(() => {
    if (!userUuid) return;
    for (const { flowKey, sessionUuid } of answeredFlows) {
      if (completedReportedRef.current.has(flowKey)) continue;
      completedReportedRef.current.add(flowKey);
      trackEvent(`dynamic-assessment-screener-completed-${flowKey}`, {
        uuid: userUuid,
        screenerName: flowKey,
        sessionUuid,
        resumed: isResumed,
      });
    }
  }, [answeredFlows, userUuid, trackEvent, isResumed]);

  // Reports this node's progress to the host — see `onProgress`'s own doc
  // comment for the contract. Runs off `content`, which the session hook only
  // ever sets to a node the member is actually shown (a question, or a gate
  // flagged `infoScreen: true`) — a silently auto-acked gate never becomes
  // `content` and so never fires this, which is fine: it carries zero weight
  // server-side, so the next node this DOES fire for already accounts for it.
  // Keyed on node identity (`flowKey` + `currentNodeExternalId`), not on
  // `content` itself, so this effect re-running for the SAME node — e.g. an
  // unstable `onProgress` identity, which is in the dependency array — doesn't
  // re-notify. Bails out BEFORE touching the ref when `onProgress` itself is
  // absent: marking a node reported without ever actually reporting it would
  // strand a host that attaches the callback only after this node already
  // resolved (a conditional prop, or a later re-render past the initial
  // mount) — it would never learn this node's progress at all.
  useEffect(() => {
    if (!content || !onProgress) return;
    const nodeKey = `${content.flowKey}:${content.uiPayload.currentNodeExternalId}`;
    if (lastReportedNodeRef.current === nodeKey) return;
    lastReportedNodeRef.current = nodeKey;
    onProgress(content.uiPayload.progress ?? null);
  }, [content, onProgress]);

  if (isLoading || shouldAdvance || !content) {
    return <LoadingScreen />;
  }

  if (content.uiPayload.infoScreen === true) {
    // A gate flagged `infoScreen: true` (camelized from the wire's
    // `info_screen` — see useDynamicAssessmentSession's auto-advance walk)
    // renders as its own static screen instead of AssessmentContent: there's
    // no question here, just the page's title/description/infoImage and a
    // Continue that sends the gate's own ack via `continueGate`. Covers both
    // a fresh session's initial node AND a resumed session parked on the
    // same flagged gate — the hook doesn't distinguish the two, so neither
    // does this branch.
    return (
      <InfoGateScreen
        key={content.sessionUuid}
        title={content.uiPayload.title}
        description={content.uiPayload.description}
        imageUrl={content.uiPayload.infoImage}
        onContinue={continueGate}
        isSubmitting={isSubmitting}
        testIDPrefix={`dynamic-assessment-${content.flowKey}-info`}
      />
    );
  }

  // `single_screen` arrives camelized (the whole session response goes
  // through humps.camelizeKeys — see api/dynamicAssessments.ts). Absent or
  // `false` leaves AssessmentContent on today's one-question-per-screen walk.
  const isSingleScreen = content.uiPayload.singleScreen === true;
  // These page-level title/description are only a per-element FALLBACK for
  // one-question-per-screen mode (an element with no title/description of
  // its own borrows the page's, so it still gets a ScreenHeading). In
  // grouped mode the page already renders its own title/description once, as
  // the single ScreenHeading — passing them through as fallbacks here would
  // duplicate that text under every element that lacks its own, so they're
  // withheld instead.
  const questions = toDynamicAssessmentQuestions(
    content.uiPayload.elements,
    isSingleScreen ? null : content.uiPayload.title,
    isSingleScreen ? null : content.uiPayload.description,
    content.uiPayload.prefilledAnswers
  );
  return (
    <AssessmentContent
      key={content.sessionUuid}
      questions={questions}
      onContinue={onContinue}
      isSubmitting={isSubmitting}
      testIDPrefix={`dynamic-assessment-${content.flowKey}`}
      singleScreen={isSingleScreen}
      pageTitle={content.uiPayload.title}
      pageDescription={content.uiPayload.description}
    />
  );
}
